import React, { FC, useState } from 'react';
import { useFormikContext } from 'formik';

import { useLazyApi } from 'Shared/services/Api';
import { useProcessOneScripts } from 'Shared/hooks';

import { FooterNextButton } from 'QuoteApp/components';
import {
  ProcessOneDataType,
  PurchaseFormValuesType,
} from 'QuoteApp/screens/Purchase/types';
import { PaymentDetailsType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';

type ProcessOneModalProps = {
  paymentDetails: PaymentDetailsType;
  disabled: boolean;
  handleDisclosure(): Promise<void>;
  handleSignaturePacketValidation(): boolean;
  handlePostAgentConfirmation(): Promise<void>;
};

const processOneContainerId = 'portalOneContainer';
const processOneContainerSelector = `#${processOneContainerId}`;

export const ProcessOneModal: FC<ProcessOneModalProps> = ({
  disabled,
  handleDisclosure,
  handleSignaturePacketValidation,
  handlePostAgentConfirmation,
}) => {
  const { details } = useAppContext();
  const { scriptLoaded } = useProcessOneScripts();
  const {
    processOneBind,
    processing,
    scrollIntoESignatureView,
  } = usePurchaseContext();
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const { values } = useFormikContext<PurchaseFormValuesType>();

  const [, requestSessionId] = useLazyApi<{ sessionId: string }>({
    url: `/payments/createProcessOneSessionId`,
    method: 'POST',
  });

  const billingZip = details.address[details.principalInsuredId].zip.value;
  const agentSignature2FeatureFlag = !!details.commands.sendSignaturePacket;

  const openProcessOne = async () => {
    setLoadingModal(true);
    try {
      if (agentSignature2FeatureFlag) {
        const attemptToSendSignaturePacket = handleSignaturePacketValidation();
        if (!attemptToSendSignaturePacket) {
          scrollIntoESignatureView();
          setLoadingModal(false);
          return;
        }
        await handlePostAgentConfirmation();
      }
      await handleDisclosure();

      const sessionResponse = await requestSessionId();

      window
        .$(processOneContainerSelector)
        .on('portalOne.load', () => setLoadingModal(false));

      window
        .$(processOneContainerSelector)
        .portalOne()
        .data('portalOne')
        .savePaymentMethod({
          paymentCategory: 'CreditCard',
          policyHolderName: details.principalInsuredName,
          billingZip: billingZip,
          confirmationDisplay: 'false',
          sessionId: sessionResponse.data.sessionId,
        });

      window
        .$(processOneContainerSelector)
        .on('portalOne.saveComplete', async function(_e: Event, data: any) {
          const processOneData: ProcessOneDataType = {
            paymentType: 'credit_card',
            cardHolderName: data.customerName,
            lastFourDigits: data.lastFourDigits,
            tokenId: data.tokenId,
            holderZip: data.holderZip,
            cardType: data.cardType,
            cardExpirationMonth: data.cardExpirationMonth,
            cardExpirationYear: data.cardExpirationYear,
          };

          await processOneBind(processOneData, values);
        });
    } catch {
      // handleDisclosure catch throws toast
      setLoadingModal(false);
    }
  };

  return (
    <>
      <FooterNextButton
        data-testid="make-process-one-payment"
        onClick={openProcessOne}
        isSubmitting={loadingModal || processing}
        disabled={!scriptLoaded || disabled}
      >
        Next: Payment
      </FooterNextButton>
      <span id={processOneContainerId} />
    </>
  );
};
